import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Chip,
  CircularProgress,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { formatMinor, isTerminal, type Order, type OrderStatus } from '@eprintsmart/shared';
import { OPEN_STATUSES, subscribeToOrders } from '../services/orders';

const STATUS_COLOUR: Partial<Record<OrderStatus, 'default' | 'primary' | 'success' | 'warning' | 'error'>> = {
  PENDING_PAYMENT: 'default',
  PAID: 'primary',
  ARTWORK_REVIEW: 'warning',
  ARTWORK_REJECTED: 'error',
  ASSIGNED: 'primary',
  VENDOR_ACCEPTED: 'primary',
  IN_PRODUCTION: 'primary',
  READY_TO_DISPATCH: 'success',
  SHIPPED: 'success',
  DELIVERED: 'success',
  CANCELLED: 'default',
  REFUNDED: 'default',
};

export function Orders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('OPEN');
  const [search, setSearch] = useState('');

  useEffect(() => subscribeToOrders((next) => {
    setOrders(next);
    setLoading(false);
  }), []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      if (filter === 'OPEN' && (isTerminal(o.status) || o.status === 'PENDING_PAYMENT')) return false;
      if (filter !== 'OPEN' && filter !== 'ALL' && o.status !== filter) return false;
      if (!q) return true;
      return (
        o.orderNumber.toLowerCase().includes(q) ||
        o.customerEmail?.toLowerCase().includes(q) ||
        o.shippingAddress?.name?.toLowerCase().includes(q)
      );
    });
  }, [orders, filter, search]);

  const counts = useMemo(() => {
    const map: Partial<Record<OrderStatus, number>> = {};
    for (const o of orders) map[o.status] = (map[o.status] ?? 0) + 1;
    return map;
  }, [orders]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="h1" sx={{ flexGrow: 1 }}>
          Orders
        </Typography>
        <TextField
          size="small"
          placeholder="Search order number, name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 300 }}
        />
        <TextField size="small" select value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ width: 190 }}>
          <MenuItem value="OPEN">Needs action</MenuItem>
          <MenuItem value="ALL">All orders</MenuItem>
          {OPEN_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {label(s)} {counts[s] ? `(${counts[s]})` : ''}
            </MenuItem>
          ))}
          <MenuItem value="DELIVERED">Delivered</MenuItem>
        </TextField>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Artwork</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Promised</TableCell>
                <TableCell align="right">Value</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell>
                    <Link component={RouterLink} to={`/orders/${order.id}`} underline="hover">
                      {order.orderNumber}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {order.shippingAddress?.name ?? '—'}
                    <Typography variant="caption" color="text.secondary" display="block">
                      {order.shippingAddress?.city}
                    </Typography>
                  </TableCell>
                  <TableCell>{order.items.length}</TableCell>
                  <TableCell>
                    <ArtworkChip order={order} />
                  </TableCell>
                  <TableCell>
                    {order.vendorIds?.length ? `${order.vendorIds.length} assigned` : (
                      <Typography variant="caption" color="text.secondary">Unassigned</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <PromisedCell order={order} />
                  </TableCell>
                  <TableCell align="right">{formatMinor(order.totals.grandTotalMinor)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={label(order.status)}
                      color={STATUS_COLOUR[order.status] ?? 'default'}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Nothing here.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}

function ArtworkChip({ order }: { order: Order }) {
  const statuses = order.items.map((i) => i.artworkStatus);
  if (statuses.every((s) => s === 'NONE')) {
    return <Typography variant="caption" color="text.secondary">Awaiting</Typography>;
  }
  if (statuses.some((s) => s === 'FAIL')) {
    return <Chip size="small" label="Failed" color="error" variant="outlined" />;
  }
  if (statuses.some((s) => s === 'WARN')) {
    return <Chip size="small" label="Check" color="warning" variant="outlined" />;
  }
  return <Chip size="small" label="Ready" color="success" variant="outlined" />;
}

/** Flags orders whose promised date is close or already past. */
function PromisedCell({ order }: { order: Order }) {
  if (!order.promisedBy) return <>—</>;

  const daysLeft = Math.ceil((order.promisedBy - Date.now()) / 86_400_000);
  const done = isTerminal(order.status);
  const date = new Date(order.promisedBy).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

  if (done) return <Typography variant="caption">{date}</Typography>;

  if (daysLeft < 0) {
    return (
      <Tooltip title={`${Math.abs(daysLeft)} day(s) overdue`}>
        <Chip size="small" label={`${date} · late`} color="error" variant="outlined" />
      </Tooltip>
    );
  }
  if (daysLeft <= 1) {
    return <Chip size="small" label={`${date} · today`} color="warning" variant="outlined" />;
  }
  return <Typography variant="caption">{date}</Typography>;
}

function label(status: OrderStatus): string {
  return status
    .toLowerCase()
    .split('_')
    .map((w, i) => (i === 0 ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(' ');
}
